/**
 * Zoom inside the frame of the product picture (QA of 7 Oct, point 12).
 *
 * MM's words: clicking the picture "feels as if you were sent to another
 * page", because the native zoom of snippets/product-media-gallery-content.liquid
 * opens a full-screen window. With a mouse, the picture now enlarges inside
 * its own frame while the cursor is over it and follows the cursor, the way
 * WooCommerce's gallery does, which is the reference MM named. Leaving the
 * frame brings it back.
 *
 * The native window stays where there is no cursor to follow: on a touch
 * screen this file does nothing and the tap opens it as before, pinch
 * included. A "click" from the keyboard opens it too, since there is no
 * cursor either.
 *
 * How the native click is held back: every slide carries
 * `on:click="#zoom-dialog-…/open/N"`, which assets/component.js serves from a
 * capture listener on `document`. This one is a capture listener on `window`,
 * the only node the event visits earlier, and stops it there.
 *
 * Loaded by blocks/_product-media-gallery.liquid when its "Zoom inside the
 * frame" setting is on. The look is assets/mm-tokens.css, section 49. If this
 * file fails to load, the click opens the native window.
 */

const SCALE = 2;
const SLIDE = 'media-gallery slideshow-slide.product-media-container--image';
const INSIDE_SLIDE_CONTROLS = 'button, a, deferred-media, product-model';
const ZOOMED_CLASS = 'mm-frame-zoom';

// How long the picture takes to close most of the distance to where it is
// going, in ms. Larger is softer. The zoom settles in about four times this.
const ZOOM_EASE = 95;
const PAN_EASE = 45;

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef {object} Zoom
 * @property {HTMLElement} slide
 * @property {number} scale - Where the zoom is now.
 * @property {number} target - Where it is going: SCALE in, 1 out.
 * @property {number} x - The point held still, in px from the slide's corner.
 * @property {number} y
 * @property {number} toX - Where the cursor is.
 * @property {number} toY
 */

/** @type {Zoom | null} */
let zoom = null;
let frame = 0;
let lastTime = 0;

/**
 * One `transform`, from the top-left corner: a translate equal to the held
 * point x (1 - scale), then the scale. That keeps the point of the picture
 * under the cursor where it is at every step, in, across and out.
 * @param {Zoom} state
 */
function paint(state) {
  const { slide, scale, x, y } = state;
  slide.style.setProperty('--mm-frame-zoom-scale', scale.toFixed(4));
  slide.style.setProperty('--mm-frame-zoom-x', `${(x * (1 - scale)).toFixed(2)}px`);
  slide.style.setProperty('--mm-frame-zoom-y', `${(y * (1 - scale)).toFixed(2)}px`);
}

/** @param {Zoom} state */
function finish(state) {
  state.slide.classList.remove(ZOOMED_CLASS);
  state.slide.style.removeProperty('--mm-frame-zoom-scale');
  state.slide.style.removeProperty('--mm-frame-zoom-x');
  state.slide.style.removeProperty('--mm-frame-zoom-y');
  if (zoom === state) zoom = null;
}

/**
 * The zoom is drawn here, frame by frame, and not by a CSS transition: a
 * transition starts over every time the cursor moves, so zooming in while
 * moving -- which is how a cursor enters a picture -- dragged behind.
 * @param {number} time
 */
function step(time) {
  frame = 0;
  if (!zoom) return;

  if (!zoom.slide.isConnected) {
    zoom = null;
    return;
  }

  const elapsed = Math.min(64, time - lastTime);
  lastTime = time;

  const instant = reducedMotion.matches;
  const zoomShare = instant ? 1 : 1 - Math.exp(-elapsed / ZOOM_EASE);
  const panShare = instant ? 1 : 1 - Math.exp(-elapsed / PAN_EASE);

  zoom.scale += (zoom.target - zoom.scale) * zoomShare;
  // On the way out the held point stays put, so the picture shrinks in place.
  if (zoom.target !== 1) {
    zoom.x += (zoom.toX - zoom.x) * panShare;
    zoom.y += (zoom.toY - zoom.y) * panShare;
  }

  const zoomSettled = Math.abs(zoom.target - zoom.scale) < 0.002;
  const panSettled = zoom.target === 1 || Math.abs(zoom.toX - zoom.x) + Math.abs(zoom.toY - zoom.y) < 0.1;

  if (zoomSettled) zoom.scale = zoom.target;

  if (zoomSettled && zoom.target === 1) {
    finish(zoom);
    return;
  }

  paint(zoom);
  // Straight to the next frame, keeping `lastTime`: going through run() here
  // would reset it and make every step a little shorter than the frame was.
  if (!zoomSettled || !panSettled) frame = requestAnimationFrame(step);
}

function run() {
  if (frame) return;
  // After a pause the first frame would count the whole pause as elapsed.
  lastTime = performance.now();
  frame = requestAnimationFrame(step);
}

/**
 * @param {HTMLElement} slide
 * @param {PointerEvent} event
 * @returns {{ x: number, y: number } | null}
 */
function pointIn(slide, event) {
  const rect = slide.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;

  return {
    x: Math.min(rect.width, Math.max(0, event.clientX - rect.left)),
    y: Math.min(rect.height, Math.max(0, event.clientY - rect.top)),
  };
}

function zoomOut() {
  if (!zoom || zoom.target === 1) return;
  zoom.target = 1;
  run();
}

/**
 * @param {HTMLElement} slide
 * @param {PointerEvent} event
 */
function zoomIn(slide, event) {
  const point = pointIn(slide, event);
  if (!point) return;

  // Another picture still on its way out goes back at once.
  if (zoom) finish(zoom);

  // The picture was chosen from its srcset for the frame; ask for the one that
  // fits the enlarged size, or the zoom only stretches the small file.
  const image = slide.querySelector('img.product-media__image');
  if (image instanceof HTMLImageElement) {
    image.sizes = `${Math.ceil(slide.getBoundingClientRect().width * SCALE)}px`;
  }

  zoom = { slide, scale: 1, target: SCALE, x: point.x, y: point.y, toX: point.x, toY: point.y };
  paint(zoom);
  slide.classList.add(ZOOMED_CLASS);
  run();
}

/**
 * The image slide the event is over, unless it is over a control inside it.
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function slideUnder(event) {
  const target = event.target;
  if (!(target instanceof Element) || target.closest(INSIDE_SLIDE_CONTROLS)) return null;

  return /** @type {HTMLElement | null} */ (target.closest(SLIDE));
}

document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches) return;

    // Leaving the frame -- for the arrows, the thumbnails, the rest of the
    // page -- ends the zoom. So does a re-render that replaced the slide:
    // `slideUnder` then returns the new one.
    const slide = slideUnder(event);
    if (!slide) {
      zoomOut();
    } else if (zoom?.slide === slide) {
      const point = pointIn(slide, event);
      if (!point) return;

      // Also picks the zoom up again if it was on its way out.
      zoom.target = SCALE;
      zoom.toX = point.x;
      zoom.toY = point.y;
      run();
    } else {
      zoomIn(slide, event);
    }
  },
  { passive: true }
);

document.documentElement.addEventListener('pointerleave', zoomOut);

document.addEventListener('keydown', (event) => {
  if (zoom && (event.key === 'Escape' || event.key.startsWith('Arrow'))) zoomOut();
});

// Hold the native full-screen window back for a click made with the mouse.
window.addEventListener(
  'click',
  (event) => {
    if (!finePointer.matches || event.detail === 0) return;
    if (slideUnder(event)) event.stopPropagation();
  },
  true
);
